/**
 * The words COMING SOON, stamped on the parts of the site that are named but
 * not built.
 *
 * The same mark as the alpha stamp next door in alpha-stamp.ts, for a related
 * job: that one says the whole site is unfinished, this one says which pieces
 * of it are not there at all. Five places. Over the facts on the front page
 * that describe something the site cannot do yet, over the Modes on Play that
 * cannot be started, under the figures on the pilot card that nothing records,
 * under the two destinations on the bar that have no address behind them, and
 * on the lobby's "Fill" - the answer to who pilots a force that means
 * "somebody we find", which is matchmaking and is `invitations`' to build.

 * Over three of them and under the other two. A stat tile is a number and a
 * six-letter caption and a bar label is one short word: the mark goes where
 * the surface has room for it, and where a surface has no middle to spare it
 * goes underneath rather than over the words. A stamp that hides the name of
 * the thing it is marking says less than no stamp at all.
 *
 * Drawn in --muted rather than in the alpha stamp's amber. The amber is that
 * stamp's alone — nothing else on the page is that colour, which is the point
 * of it — and every other "there is nothing here yet" mark the site already
 * has is in the muted ink: the future mode cards on Play, the pilot's
 * unrecorded figures. The look is in styles.css under `.stamp-soon`.
 *
 * The bar's copy is not built here. It has to be in the HTML the server sends,
 * because the blog's pages never run the app, so layouts/Base.astro writes the
 * same span by hand. scripts/soon-stamp.test.mjs keeps the copies together.
 */

import { el } from "./dom";

/** Uppercased by the stylesheet, so this is the words and not a shout. */
export const SOON = "Coming soon";

/**
 * A stamp for something the site names but has not built. `variant` picks the
 * surface: "fact" is the front page's strip, "mode" a Mode on Play that cannot
 * be started, "tile" a figure on the pilot card that nothing records, "seat"
 * one answer of the lobby's four-answer seat control.
 *
 * The first two lie over their card and are sized off it; the third sits under
 * its caption, because a stat tile has no middle to lay anything across; the
 * fourth lies across a button one word wide, at a size and an angle that leave
 * the word readable under it. So the variant is a size and a place, and never
 * a different mark.
 *
 * Hidden from screen readers, all four. The mark is an angle and a colour,
 * neither of which is anything to one, and there is nothing here to act on:
 * what a reader gets instead is the "(not ready yet)" the card's own heading,
 * the tile's own caption or the button's own label carries, which is in the
 * reading order where the name is rather than wherever the stamp landed.
 */
export function soonStamp(
  variant: "fact" | "mode" | "tile" | "seat",
): HTMLElement {
  return el("span", {
    className: `stamp stamp-soon stamp-${variant}`,
    textContent: SOON,
    "aria-hidden": "true",
  });
}

/**
 * The note the stamp cannot give a screen reader, for a heading to carry.
 *
 * Written once here because three callers say it and a fourth will: the two
 * screens below, and layouts/Base.astro's bar, which spells it out by hand for
 * the same reason it spells out the stamp.
 */
export function notReadyNote(): HTMLElement {
  return el("span", {
    className: "visually-hidden",
    textContent: " (not ready yet)",
  });
}
